/**
 * F-114 (#114) US3 — render-time placeholder injection + offset remap (FR-020,
 * the crux). Pure, zero-infra (Constitution §III); the entire FR-020 difficulty
 * lives here so it can be exhaustively unit- and mutation-tested without a DOM.
 *
 * Given the stored RAW General Instructions `body` (tokens intact), its
 * placeholder spans, the F-049 formatting/highlight sidecars, and the per-trial
 * value map, it returns the RENDERED body plus those sidecars remapped onto it:
 *
 *   - each placeholder is replaced by `values.get(canonicalId)` or, when
 *     unresolved, its `displayName` — never a raw `{…}` token (FR-018, SC-005);
 *   - the rendered string is NEVER re-scanned, so a value containing braces is
 *     literal (FR-014, I-PC-07);
 *   - formatting/highlight spans are remapped through a piecewise offset map so
 *     cues stay on the correct words for longer / shorter / repeated / adjacent
 *     replacements (FR-020, SC-008): a span fully covering a token grows/shrinks
 *     with the replacement; a partial overlap clamps to the literal portion.
 *
 * Offsets are 0-based Unicode CODE POINTS, half-open `[offset, offset + length)`
 * — the F-049 convention, so `MarksView` consumes the result unchanged.
 */
import { clampToBody } from './spans.js';
import type {
  EditingHintHighlight,
  FormattingMark,
  PlaceholderSpan,
  PlaceholderValues,
  Span,
} from './types.js';

/** The rendered General Instructions: body + sidecars, all over the rendered string. */
export type ResolvedInstructions = {
  readonly body: string;
  readonly formatting: readonly FormattingMark[];
  readonly editingHintHighlights: readonly EditingHintHighlight[];
};

/** One resolved token: its original code-point span `[a, b)` and the value's length. */
type Token = {
  readonly a: number;
  readonly b: number;
  readonly valueLen: number;
};

export const resolvePlaceholders = (
  body: string,
  placeholders: readonly PlaceholderSpan[],
  formatting: readonly FormattingMark[],
  highlights: readonly EditingHintHighlight[],
  values: PlaceholderValues,
): ResolvedInstructions => {
  const bodyCodePoints = [...body];
  const sorted = [...placeholders].sort((a, b) => a.offset - b.offset);

  // Walk left→right: copy literal runs, substitute each token with its value, and
  // record each token's original span + the replacement's code-point length.
  const tokens: Token[] = [];
  const pieces: string[] = [];
  let cursor = 0;
  for (const placeholder of sorted) {
    if (placeholder.offset > cursor) {
      pieces.push(bodyCodePoints.slice(cursor, placeholder.offset).join(''));
    }
    const value = values.get(placeholder.canonicalId) ?? placeholder.displayName;
    pieces.push(value);
    tokens.push({
      a: placeholder.offset,
      b: placeholder.offset + placeholder.length,
      valueLen: [...value].length,
    });
    cursor = placeholder.offset + placeholder.length;
  }
  if (cursor < bodyCodePoints.length) {
    pieces.push(bodyCodePoints.slice(cursor).join(''));
  }
  const renderedBody = pieces.join('');
  const renderedLen = [...renderedBody].length;

  // The rendered code-point position of a LITERAL/boundary original offset `o`:
  // shift it by the cumulative length delta of every token that ends at or before
  // it. (Tokens are sorted + non-overlapping, so the scan stops early.)
  const renderedAt = (o: number): number => {
    let delta = 0;
    for (const token of tokens) {
      if (token.b <= o) {
        delta += token.valueLen - (token.b - token.a);
      } else {
        break;
      }
    }
    return o + delta;
  };
  const tokenContaining = (o: number): Token | undefined =>
    tokens.find((token) => token.a < o && o < token.b);

  // A span START strictly inside a token clamps to the token's value END (its
  // literal tail); a span END strictly inside clamps to the value START (its
  // literal head) — so a partial overlap keeps only the literal portion.
  const mapStart = (o: number): number => {
    const inside = tokenContaining(o);
    return inside ? renderedAt(inside.b) : renderedAt(o);
  };
  const mapEnd = (o: number): number => {
    const inside = tokenContaining(o);
    return inside ? renderedAt(inside.a) : renderedAt(o);
  };
  const remap = <T extends Span>(span: T): T => {
    const start = mapStart(span.offset);
    const stop = mapEnd(span.offset + span.length);
    return { ...span, offset: start, length: Math.max(0, stop - start) };
  };

  return {
    body: renderedBody,
    formatting: clampToBody(formatting.map(remap), renderedLen),
    editingHintHighlights: clampToBody(highlights.map(remap), renderedLen),
  };
};
